import { useId } from 'react'

// Combat drone glyph: the wasp, head up, in two poses — 'spread' (public/
// wasp-B-spread.svg, wings out: veterans in the air) and 'folded'
// (public/wasp-C-folded.svg, wings laid back along the body, longer legs:
// new drones on the ground, landed airdrops, a mature Brood's side drones). Corridor units are aircraft — drones — in all player-facing
// fiction; the component keeps its legacy name because code identifiers stay
// `missile*`.
//
// DronePaths is the shared geometry, drawn in the 64 × 64 box every caller
// uses: the source's 120 × 108 artwork scaled to the full width and centred
// top to bottom. One colour does everything — body solid, wings a 30% wash
// with a solid edge, legs and antennae as lines. The two halves are the same
// drawing mirrored; the abdomen's bands are clipped to its outline (its clip
// id is per-instance, so many drones on one page don't share one).
const WASP_SCALE = 64 / 120
const WASP_LIFT = (64 - 108 * WASP_SCALE) / 2

function WaspHalf({ fill, pose }) {
    if (pose === 'folded') return <WaspHalfFolded fill={fill} />
    return (
        <g stroke={fill} strokeLinecap="round" strokeLinejoin="round" fill="none">
            <path d="M57.5 16.5 Q55 15 53 12.5 L47.5 7.5 Q46 6.3 42.5 6.4" strokeWidth="2.3" />
            <path d="M53 31 L4 25.5 C0.5 25.3 0.5 31.2 4 31.6 L53 37 Z" fill={fill} fillOpacity="0.3" strokeWidth="1.5" />
            <path d="M53 39 L15 41.5 C11.5 41.8 12 46.6 15.5 46.2 L53 42 Z" fill={fill} fillOpacity="0.3" strokeWidth="1.5" />
            <path d="M53 32 L46 26 L43 18 L40 14" strokeWidth="2.3" />
            <path d="M52 39 L41 44 L35 54 L30 58" strokeWidth="2.3" />
            <path d="M53 45 L44 54 L41 70 L36 80" strokeWidth="2.3" />
            <g fill={fill} stroke="none">
                <circle cx="46" cy="26" r="1.7" /><circle cx="43" cy="18" r="1.4" />
                <circle cx="41" cy="44" r="1.7" /><circle cx="35" cy="54" r="1.4" />
                <circle cx="44" cy="54" r="1.8" /><circle cx="41" cy="70" r="1.5" />
            </g>
        </g>
    )
}

function WaspHalfFolded({ fill }) {
    return (
        <g stroke={fill} strokeLinecap="round" strokeLinejoin="round" fill="none">
            <path d="M57.5 16.5 Q55 15 53 12.5 L47.5 7.5 Q46 6.3 42.5 6.4" strokeWidth="2.3" />
            <path d="M53 32 L45 25 L42 16 L38 11" strokeWidth="2.3" />
            <path d="M52 39 L39 44 L31 55 L25 60" strokeWidth="2.3" />
            <path d="M53 45 L42 55 L38 74 L32 88" strokeWidth="2.3" />
            <g fill={fill} stroke="none">
                <circle cx="45" cy="25" r="1.7" /><circle cx="42" cy="16" r="1.4" />
                <circle cx="39" cy="44" r="1.7" /><circle cx="31" cy="55" r="1.4" />
                <circle cx="42" cy="55" r="1.8" /><circle cx="38" cy="74" r="1.5" />
            </g>
            <path d="M54 31 L39 80 C38 84 41.5 85.5 43.5 82 L55.5 37 Z" fill={fill} fillOpacity="0.3" strokeWidth="1.5" />
        </g>
    )
}

export function DronePaths({ fill, pose = 'spread' }) {
    const clip = `wasp-abd-${useId().replace(/:/g, '')}`
    return (
        <g transform={`translate(0 ${WASP_LIFT.toFixed(2)}) scale(${WASP_SCALE.toFixed(4)})`} fill={fill}>
            <defs>
                <clipPath id={clip}>
                    <path d="M60 54 C68 55 72 63 72 72 C72 84 66 94 60 104 C54 94 48 84 48 72 C48 63 52 55 60 54 Z" />
                </clipPath>
            </defs>
            <WaspHalf fill={fill} pose={pose} />
            <g transform="translate(120 0) scale(-1 1)"><WaspHalf fill={fill} pose={pose} /></g>
            <ellipse cx="60" cy="22" rx="8" ry="6.5" />
            <ellipse cx="60" cy="38" rx="9" ry="11" />
            <rect x="58.8" y="48" width="2.4" height="7" rx="1" />
            <g clipPath={`url(#${clip})`}>
                <path d="M40 50 H80 V66.5 Q60 71.5 40 66.5 Z" />
                <path d="M40 70 Q60 75 80 70 V77.5 Q60 82.5 40 77.5 Z" />
                <path d="M40 81 Q60 86 80 81 V88.5 Q60 93.5 40 88.5 Z" />
                <path d="M40 92 Q60 97 80 92 V110 H40 Z" />
            </g>
        </g>
    )
}

export default function MissileIcon({ size = 15, color = 'currentColor', pose = 'spread', style, className }) {
    return (
        <svg
            viewBox="0 0 64 64"
            style={{ width: size, height: size, display: 'block', fill: color, flexShrink: 0, ...style }}
            className={className}
        >
            <DronePaths fill={color} pose={pose} />
        </svg>
    )
}

// ─── The shot in flight: wasp A, wings flapping ───────────────────────────────
// public/wasp-A-flap.svg (head up, wings swept back and beating: two blurred
// wing poses swapped 16 times a second over a smeared ghost of the stroke),
// outlined all round. `fill` paints the body (a colour, or a url(#gradient));
// `outline` rings every part at OUTLINE_W either side, behind the fill, so
// the abdomen's band gaps show the outline colour (black stripes on yours).
// Drawn in its 120 × 108 box; ids are per instance (many shots fly at once).
const OUTLINE_W = 3.5
const ABDOMEN = 'M60 54 C68 55 72 63 72 72 C72 84 66 94 60 104 C54 94 48 84 48 72 C48 63 52 55 60 54 Z'
const WING_A = ['M53 31 L6 56 C2.5 58 4 63 8.5 61.5 L53 38 Z', 'M53 40 L20 64 C17 66 19 69.5 22.5 67.8 L53 43.5 Z']
const LEGS_A = ['M53 31 L47 32.5 L44.5 39 L43.5 45', 'M52 39 L41 44 L35 54 L30 58', 'M53 45 L44 54 L41 70 L36 80']
const JOINTS_A = [[47, 32.5, 1.7], [44.5, 39, 1.4], [41, 44, 1.7], [35, 54, 1.4], [44, 54, 1.8], [41, 70, 1.5]]
const ANTENNA_A = 'M57.5 17.5 Q55.5 17 53.5 15.5 L47.5 12.5 Q45.5 11.8 42 15'

function WingPose({ angle, fill, outline }) {
    return (
        <g transform={`rotate(${angle} 53 36)`} strokeLinejoin="round">
            {WING_A.map(d => <path key={`o${d}`} d={d} fill="none" stroke={outline} strokeWidth={1.5 + 2 * OUTLINE_W} />)}
            {WING_A.map(d => <path key={d} d={d} fill={fill} fillOpacity="0.45" stroke={fill} strokeWidth="1.5" />)}
        </g>
    )
}

function WaspFlapHalf({ fill, outline, blur, smear }) {
    const line = (d, w, color) => <path key={`${color}${d}`} d={d} fill="none" stroke={color} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
    return (
        <g>
            {line(ANTENNA_A, 2.3 + 2 * OUTLINE_W, outline)}
            {LEGS_A.map(d => line(d, 2.3 + 2 * OUTLINE_W, outline))}
            {JOINTS_A.map(([x, y, r]) => <circle key={`o${x}${y}`} cx={x} cy={y} r={r + OUTLINE_W} fill={outline} />)}
            {/* the beat: a smeared ghost of the stroke under two blurred poses, swapped */}
            <g filter={`url(#${smear})`} opacity="0.5">
                {[-12, -6, 0, 5].map(a => (
                    <g key={a} transform={`rotate(${a} 53 36)`}>
                        {WING_A.map(d => <path key={d} d={d} fill={fill} fillOpacity="0.3" />)}
                    </g>
                ))}
            </g>
            <g filter={`url(#${blur})`}>
                <WingPose angle={5} fill={fill} outline={outline} />
                <animate attributeName="opacity" values="1;0" dur="0.06s" calcMode="discrete" repeatCount="indefinite" />
            </g>
            <g filter={`url(#${blur})`} opacity="0">
                <WingPose angle={-12} fill={fill} outline={outline} />
                <animate attributeName="opacity" values="0;1" dur="0.06s" calcMode="discrete" repeatCount="indefinite" />
            </g>
            {line(ANTENNA_A, 2.3, fill)}
            {LEGS_A.map(d => line(d, 2.3, fill))}
            {JOINTS_A.map(([x, y, r]) => <circle key={`f${x}${y}`} cx={x} cy={y} r={r} fill={fill} />)}
        </g>
    )
}

// `gradient` / `outlineGradient` ([[offset, colour], …], head to tail) paint
// the body or the outline in a gradient instead of a flat colour.
export function WaspFlap({ fill, outline, gradient = null, outlineGradient = null }) {
    const id = useId().replace(/:/g, '')
    const clip = `wa-abd-${id}`, blur = `wa-blur-${id}`, smear = `wa-smear-${id}`, grad = `wa-grad-${id}`, ograd = `wa-ograd-${id}`
    const paint = gradient ? `url(#${grad})` : fill
    if (outlineGradient) outline = `url(#${ograd})`
    return (
        <g>
            <defs>
                <clipPath id={clip}><path d={ABDOMEN} /></clipPath>
                <filter id={blur} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="0.7" /></filter>
                <filter id={smear} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="1.6" /></filter>
                {gradient && (
                    <linearGradient id={grad} gradientUnits="userSpaceOnUse" x1="0" y1="10" x2="0" y2="104">
                        {gradient.map(([o, c]) => <stop key={o} offset={o} stopColor={c} />)}
                    </linearGradient>
                )}
                {outlineGradient && (
                    <linearGradient id={ograd} gradientUnits="userSpaceOnUse" x1="0" y1="6" x2="0" y2="108">
                        {outlineGradient.map(([o, c]) => <stop key={o} offset={o} stopColor={c} />)}
                    </linearGradient>
                )}
            </defs>
            {/* the body's outline, behind everything it rings */}
            <g fill={outline} stroke={outline} strokeWidth={2 * OUTLINE_W} strokeLinejoin="round">
                <ellipse cx="60" cy="22" rx="8" ry="6.5" />
                <ellipse cx="60" cy="38" rx="9" ry="11" />
                <rect x="58.8" y="48" width="2.4" height="7" rx="1" />
                <path d={ABDOMEN} />
            </g>
            <WaspFlapHalf fill={paint} outline={outline} blur={blur} smear={smear} />
            <g transform="translate(120 0) scale(-1 1)"><WaspFlapHalf fill={paint} outline={outline} blur={blur} smear={smear} /></g>
            <g fill={paint}>
                <ellipse cx="60" cy="22" rx="8" ry="6.5" />
                <ellipse cx="60" cy="38" rx="9" ry="11" />
                <rect x="58.8" y="48" width="2.4" height="7" rx="1" />
                <g clipPath={`url(#${clip})`}>
                    <path d="M40 50 H80 V66.5 Q60 71.5 40 66.5 Z" />
                    <path d="M40 70 Q60 75 80 70 V77.5 Q60 82.5 40 77.5 Z" />
                    <path d="M40 81 Q60 86 80 81 V88.5 Q60 93.5 40 88.5 Z" />
                    <path d="M40 92 Q60 97 80 92 V110 H40 Z" />
                </g>
            </g>
        </g>
    )
}
